import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiX, FiDownload, FiFileText, FiTable, FiFilter, FiGlobe, FiFile } from 'react-icons/fi';
import {
  DEFAULT_TABLE_COLUMN_SELECTION,
  EXPORT_TABLE_COLUMNS,
} from './compliance-ui/complianceExport';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ModeOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 16px;
  background: ${(p) => (p.$selected ? 'rgba(233,112,36,0.12)' : 'white')};
  border: 2px solid ${(p) => (p.$selected ? '#e97024' : '#e5e7eb')};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: #e97024;
  }
`;

const ModeTitle = styled.div`
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
`;

const FormatOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const FormatOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${(p) => (p.$selected ? 'rgba(233,112,36,0.12)' : 'white')};
  border: 2px solid ${(p) => (p.$selected ? '#e97024' : '#e5e7eb')};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #111827;

  &:hover {
    border-color: #e97024;
  }
`;

const ColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
`;

const ColumnOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #f3f4f6;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #e97024;
    box-shadow: 0 0 0 3px rgba(233, 112, 36, 0.1);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const ExportButton = styled(Button)`
  background: #e97024;
  color: white;

  &:hover:not(:disabled) {
    background: #e55a2b;
  }
`;

const REPORT_FORMATS = [
  { key: 'html', title: 'HTML', icon: <FiGlobe /> },
  { key: 'pdf', title: 'PDF', icon: <FiFileText /> },
];

const TABLE_FORMATS = [
  { key: 'csv', title: 'CSV', icon: <FiTable /> },
  { key: 'excel', title: 'Excel', icon: <FiFile /> },
  { key: 'html', title: 'HTML table', icon: <FiGlobe /> },
  { key: 'pdf', title: 'PDF', icon: <FiFileText /> },
];

export default function ExportOptionsModal({
  isOpen,
  onClose,
  onExport,
  issues,
  filteredIssues,
}) {
  const [exportMode, setExportMode] = useState('report');
  const [selectedFormat, setSelectedFormat] = useState('html');
  const [selectedColumns, setSelectedColumns] = useState({ ...DEFAULT_TABLE_COLUMN_SELECTION });
  const [exportScope, setExportScope] = useState('filtered');
  const [includeImages, setIncludeImages] = useState(false);

  const formats = exportMode === 'report' ? REPORT_FORMATS : TABLE_FORMATS;
  const dataToExport = exportScope === 'all' ? issues : filteredIssues;
  const issueCount = dataToExport?.length ?? 0;

  useEffect(() => {
    if (!isOpen) return;
    setExportMode('report');
    setSelectedFormat('html');
    setSelectedColumns({ ...DEFAULT_TABLE_COLUMN_SELECTION });
    setExportScope('filtered');
    setIncludeImages(false);
  }, [isOpen]);

  useEffect(() => {
    const allowed = formats.map((f) => f.key);
    if (!allowed.includes(selectedFormat)) {
      setSelectedFormat(allowed[0]);
    }
  }, [exportMode, selectedFormat, formats]);

  const selectedColumnKeys = useMemo(
    () => Object.keys(selectedColumns).filter((key) => selectedColumns[key]),
    [selectedColumns]
  );

  const canExport =
    issueCount > 0 && (exportMode === 'report' || selectedColumnKeys.length > 0);

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((prev) => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const handleExport = () => {
    if (!canExport) return;
    onExport({
      mode: exportMode,
      format: selectedFormat,
      columns: selectedColumnKeys,
      data: dataToExport,
      includeImages: exportMode === 'report' && includeImages,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiDownload size={20} />
            Export
          </ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle>Export type</SectionTitle>
            <ModeOptions>
              <ModeOption
                type="button"
                $selected={exportMode === 'report'}
                onClick={() => setExportMode('report')}
              >
                <ModeTitle>
                  <FiFileText />
                  Report
                </ModeTitle>
                <ModeDescription>
                  Continuous narrative per issue: requirements, research results, evidence,
                  resolutions, and images.
                </ModeDescription>
              </ModeOption>
              <ModeOption
                type="button"
                $selected={exportMode === 'table'}
                onClick={() => setExportMode('table')}
              >
                <ModeTitle>
                  <FiTable />
                  Table
                </ModeTitle>
                <ModeDescription>
                  Spreadsheet-style export matching the dashboard table columns.
                </ModeDescription>
              </ModeOption>
            </ModeOptions>
          </Section>

          <Section>
            <SectionTitle>
              <FiFilter size={16} />
              Issues to include
            </SectionTitle>
            <FilterSelect
              value={exportScope}
              onChange={(e) => setExportScope(e.target.value)}
            >
              <option value="filtered">
                Current filtered list ({filteredIssues?.length ?? 0} issues)
              </option>
              <option value="all">All project issues ({issues?.length ?? 0} issues)</option>
            </FilterSelect>
          </Section>

          <Section>
            <SectionTitle>File format</SectionTitle>
            <FormatOptions>
              {formats.map((format) => (
                <FormatOption
                  key={format.key}
                  type="button"
                  $selected={selectedFormat === format.key}
                  onClick={() => setSelectedFormat(format.key)}
                >
                  {format.icon}
                  {format.title}
                </FormatOption>
              ))}
            </FormatOptions>
          </Section>

          {exportMode === 'table' ? (
            <Section>
              <SectionTitle>Columns</SectionTitle>
              <ColumnGrid>
                {EXPORT_TABLE_COLUMNS.map((column) => (
                  <ColumnOption key={column.key}>
                    <input
                      type="checkbox"
                      checked={!!selectedColumns[column.key]}
                      onChange={() => handleColumnToggle(column.key)}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>{column.label}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{column.description}</div>
                    </div>
                  </ColumnOption>
                ))}
              </ColumnGrid>
            </Section>
          ) : (
            <Section>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={includeImages}
                  onChange={(e) => setIncludeImages(e.target.checked)}
                />
                Include image URLs in the report
              </CheckboxRow>
            </Section>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
          <ExportButton type="button" onClick={handleExport} disabled={!canExport}>
            <FiDownload size={16} />
            Export {exportMode === 'report' ? 'report' : 'table'}
            {issueCount > 0 ? ` (${issueCount})` : ''}
          </ExportButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
